import { useEffect, useId, useRef } from 'react';
import type { ChangeEvent, InputHTMLAttributes, JSX, ReactNode } from 'react';
import { useFieldAria } from './Field';
import { cx } from './styles';
import type { TriState } from './Checkbox';

export type SwitchSize = 'sm' | 'md';

export interface SwitchProps
  extends Omit<
    InputHTMLAttributes<HTMLInputElement>,
    'aria-describedby' | 'checked' | 'children' | 'className' | 'onChange' | 'size' | 'type' | 'value'
  > {
  value: TriState;
  onChange: (next: boolean) => void;
  label?: ReactNode;
  description?: ReactNode;
  size?: SwitchSize;
  disabled?: boolean;
  className?: string;
  id?: string;
  'aria-describedby'?: string;
}

const TRACK: Record<SwitchSize, string> = { sm: 'h-4 w-7', md: 'h-5 w-9' };
const KNOB: Record<SwitchSize, string> = { sm: 'size-3', md: 'size-4' };
const KNOB_ON: Record<SwitchSize, string> = { sm: 'translate-x-3', md: 'translate-x-4' };
const KNOB_UNKNOWN: Record<SwitchSize, string> = { sm: 'translate-x-1.5', md: 'translate-x-2' };

/**
 * 开关。用于"立即生效"的布尔状态（复选框用于"随表单一起提交"的选项）。
 *
 * 三态的处理方式和 `Checkbox` 一致，但开关没有"横杠"这个原生表现，所以未知态
 * 靠**三个通道**同时表达，任何一条失效都还读得出来：
 *   1. `aria-checked="mixed"`（读屏器）；
 *   2. 滑块停在**中间**（位置，色觉无关）；
 *   3. 轨道描边为**虚线** + 中性色（形状 + 颜色）。
 *
 * 底子是原生 `input`：键盘（空格/回车）、焦点、读屏都走浏览器实现，
 * 视觉部分才由我们自己画。手感上"看起来是开关"，语义上"就是个复选框"。
 */
export function Switch({
  value,
  onChange,
  label,
  description,
  size = 'md',
  disabled = false,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: SwitchProps): JSX.Element {
  const field = useFieldAria();
  const generatedId = useId().replaceAll(':', '');
  const controlId = field === null ? (id ?? `switch-${generatedId}`) : field.controlId;
  const descriptionId = description === undefined ? undefined : `${controlId}-description`;
  const inputRef = useRef<HTMLInputElement>(null);
  const unknown = value === 'unknown';
  const on = value === true;
  const isDisabled = disabled || field?.disabled === true;
  const describedBy = cx(field?.describedBy, descriptionId, ariaDescribedBy);

  useEffect(() => {
    // 与 Checkbox 同理：`indeterminate` 没有 HTML 属性，只能命令式投影
    if (inputRef.current !== null) inputRef.current.indeterminate = unknown;
  }, [unknown]);

  function handleChange(event: ChangeEvent<HTMLInputElement>): void {
    onChange(event.target.checked);
  }

  const control = (
    <span className="relative inline-flex shrink-0 items-center">
      <input
        {...rest}
        ref={inputRef}
        id={controlId}
        type="checkbox"
        role="switch"
        checked={on}
        aria-checked={unknown ? 'mixed' : on}
        onChange={handleChange}
        disabled={isDisabled}
        aria-describedby={describedBy === '' ? undefined : describedBy}
        aria-invalid={field?.invalid === true ? true : undefined}
        // 视觉全部由后面的轨道/滑块承担；输入框本身只留下可聚焦的那一份语义
        className="peer sr-only"
      />
      <span
        aria-hidden="true"
        data-state={unknown ? 'unknown' : on ? 'on' : 'off'}
        className={cx(
          'rounded-full border transition-colors peer-disabled:opacity-50',
          TRACK[size],
          on
            ? 'border-accent bg-accent'
            : cx('border-line-strong bg-surface-3', unknown && 'border-dashed'),
          'peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-(--focus-ring)',
        )}
      />
      <span
        aria-hidden="true"
        className={cx(
          'pointer-events-none absolute left-0.5 rounded-full transition-transform',
          KNOB[size],
          on ? 'bg-accent-fg' : unknown ? 'bg-fg-muted' : 'bg-fg-subtle',
          unknown ? KNOB_UNKNOWN[size] : on ? KNOB_ON[size] : '',
        )}
      />
    </span>
  );

  if (label === undefined && description === undefined) return control;

  return (
    <div className={cx('flex flex-col gap-0.5', className)}>
      {label === undefined ? (
        control
      ) : (
        <label
          htmlFor={controlId}
          className={cx('inline-flex items-center gap-2 text-sm text-fg', isDisabled && 'opacity-50')}
        >
          {control}
          <span>{label}</span>
        </label>
      )}
      {description === undefined ? null : (
        <p id={descriptionId} className="m-0 pl-9 text-xs text-fg-subtle">
          {description}
        </p>
      )}
    </div>
  );
}
